import React, { useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/** Public complaint-capture form on a business mini-site (shops with the `complaints` capability).
 * Posts to the city-scoped, per-business-throttled capture endpoint; the business is named by id in
 * the path, the city by the signed city-token — never by the form. A phone or email is required so
 * the shop can reply. Mirrors EnquirySection. */
export function ComplaintSection({ business }) {
  const { t } = useApp();
  const [form, setForm] = useState({
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    subject: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const canSend = form.contact_phone.trim() !== "" || form.contact_email.trim() !== "";

  const submit = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      await api.post(`/city/businesses/${business.id}/complaints`, {
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim(),
        contact_email: form.contact_email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setForm({ contact_name: "", contact_phone: "", contact_email: "", subject: "", message: "" });
      setSent(true);
      toast.success(t("complaint.sent"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-border p-6 text-center" data-testid="complaint-sent">
        <p className="text-sm text-muted-foreground">{t("complaint.sentBody")}</p>
        <Button
          variant="outline"
          className="mt-4 rounded-full"
          data-testid="complaint-another"
          onClick={() => setSent(false)}
        >
          {t("complaint.sendAnother")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-border p-5" data-testid="complaint-section">
      <p className="text-sm text-muted-foreground">{t("complaint.intro")}</p>
      <div className="space-y-1.5">
        <Label htmlFor="cmp-name">{t("complaint.name")}</Label>
        <Input
          id="cmp-name"
          data-testid="complaint-name"
          value={form.contact_name}
          onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="cmp-phone">{t("complaint.phone")}</Label>
          <Input
            id="cmp-phone"
            data-testid="complaint-phone"
            value={form.contact_phone}
            onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cmp-email">{t("complaint.email")}</Label>
          <Input
            id="cmp-email"
            type="email"
            data-testid="complaint-email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cmp-subject">{t("complaint.subject")}</Label>
        <Input
          id="cmp-subject"
          data-testid="complaint-subject"
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cmp-message">{t("complaint.message")}</Label>
        <Textarea
          id="cmp-message"
          rows={4}
          data-testid="complaint-message"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
        />
      </div>
      <Button
        className="rounded-full"
        disabled={busy || !canSend}
        data-testid="complaint-submit"
        onClick={submit}
      >
        {t("complaint.submit")}
      </Button>
    </div>
  );
}
